import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus } from 'src/api/v2';
import { useUser } from 'src/features/authorization';
import useOneWayIn from '../../hooks/use-one-way-in';
import { toast } from 'react-toastify';
import createOwiUpdateDto from '../../opportunity-details/utils/createOwiUpdateDto';
import ToastContent from 'src/components/ToastContent/ToastContent';
import { TextArea } from '@innovationnorway/component-library';
import { Button, Heading, Tabs } from '@innovationnorway/cl-react';
import { LoadingPage } from 'src/components/LoadingPage/LoadingPage';
import { RaskAvklaringViser } from '@innovationnorway/kunde-raskavklaring-visning';

interface Props {
  opportunity: OneWayInOpportunitySimpleV2Dto;
}

type TabTypes = 'form' | 'other-feedbacks';

const OneWayInNoteListBody: React.FC<Props> = ({ opportunity }) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tCommon } = useTranslation('common');
  const { isAadUser, user, displayName } = useUser();
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState(false);

  const {
    oneWayInOpportunityFormFullHistoryQuery,
    oneWayInGroupedFormWOEnrollmentsQuery,
    updateOneWayInOpportunityStatus,
  } = useOneWayIn(opportunity.oneWayInId);

  const [tab, setTab] = React.useState<TabTypes>('form');

  const onAddNote = async () => {
    if (note === '') {
      setNoteError(true);
      return;
    }
    await updateOneWayInOpportunityStatus
      .mutateAsync(
        createOwiUpdateDto(
          opportunity?.id,
          OneWayInStatus.Active,
          note,
          isAadUser ? undefined : user?.clusterUserId,
          displayName || undefined,
        ),
      )
      .then(() => {
        toast(
          <ToastContent
            success={true}
            title={tOneWayIn('customerFollowUp.addNote.toast.success.title')}
            text={tOneWayIn('review.addNote.success')}
          />,
          {
            toastId: 'NoteAdded',
            autoClose: false,
            position: 'bottom-center',
          },
        );
        setNote('');
      })
      .catch(() => {
        toast(
          <ToastContent
            success={false}
            title={tOneWayIn('customerFollowUp.addNote.toast.error.title')}
            text={tOneWayIn('customerFollowUp.addNote.toast.error.text')}
          />,
          {
            toastId: 'NoteError',
            autoClose: false,
            position: 'bottom-center',
          },
        );
      });
    setNoteError(false);
  };

  return (
    <div>
      <div className="margin-top--1 margin-bottom--4">
        <TextArea
          label={`${tCommon('note')}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxCount={1000}
          maxLength={1000}
          errorMsg={noteError ? tOneWayIn('details.AddNote.Required') : undefined}
        />
        <Button
          type="button"
          variant="secondary"
          onClick={onAddNote}
          loading={updateOneWayInOpportunityStatus.isLoading}
          disabled={updateOneWayInOpportunityStatus.isLoading}
        >
          {tCommon('add')}
        </Button>
      </div>
      <div className="margin-bottom--4">
        <Tabs value={tab}>
          <Tabs.List>
            <Tabs.Tab value="form" onClick={() => setTab('form')} aria-label={tOneWayIn('tab.Form')}>
              {tOneWayIn('tab.Form')}
            </Tabs.Tab>
            <Tabs.Tab
              value="other-feedbacks"
              onClick={() => setTab('other-feedbacks')}
              aria-label={tOneWayIn('feedbacks')}
            >
              {tOneWayIn('feedbacks')}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </div>
      {tab === 'form' && (
        <>
          {oneWayInGroupedFormWOEnrollmentsQuery.isFetching && <LoadingPage />}
          {oneWayInGroupedFormWOEnrollmentsQuery.data &&
            !oneWayInGroupedFormWOEnrollmentsQuery.isFetching && (
              <RaskAvklaringViser
                id={oneWayInGroupedFormWOEnrollmentsQuery.data?.id ?? ''}
                tittel={oneWayInGroupedFormWOEnrollmentsQuery.data?.tittel ?? ''}
                grupper={oneWayInGroupedFormWOEnrollmentsQuery.data?.grupper ?? []}
                organisasjon={oneWayInGroupedFormWOEnrollmentsQuery.data?.organisasjon}
                kontakt={oneWayInGroupedFormWOEnrollmentsQuery.data?.kontakt}
                innsendtDato={oneWayInGroupedFormWOEnrollmentsQuery.data?.innsendtDato || ''}
              />
            )}
        </>
      )}

      {tab === 'other-feedbacks' &&
        oneWayInOpportunityFormFullHistoryQuery.data
          ?.filter((historyItem) => historyItem.description != null && historyItem.description.length > 0)
          .map((historyItem) => (
            <div key={historyItem.id}>
              <Heading level={3}>{historyItem.clusterName}</Heading>
              <p>{historyItem.description}</p>
            </div>
          ))}
    </div>
  );
};

export default OneWayInNoteListBody;
